import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { ENDPOINTS } from '../shared/constants/url.constants';
import { HttpRequestService } from '../shared/services/http-request.service';
import { GlobalErrorHandlerService } from '../shared/services/global-error-handler.service';
import { ListGridRequestPayload } from '../shared/interfaces/list.interface';

/** TS = timesheet lines, IV = invoice lines. */
export type CorrectionDataType = 'TS' | 'IV';

/** Per-line outcome returned by the payroll correction APIs. */
export interface CorrectionLineResult {
  dataId: number;
  /** STE = success, ER = error/refused (see `reason`). */
  status: 'STE' | 'ER' | string;
  reason?: string;
  modifiedBy?: number;
  modifiedByName?: string;
  modifiedDate?: string;
}

export const CORRECTION_STATUS_SUCCESS = 'STE';

/** Per-line results from a correction API response, which may come wrapped in responsedata or as a bare array. */
export function correctionResults(response: any): CorrectionLineResult[] {
  return Array.isArray(response) ? response : response?.responsedata ?? [];
}

@Injectable({
  providedIn: 'root',
})
export class PayrollCorrectionService {
  constructor(
    private httpClient: HttpRequestService,
    private errorHandler: GlobalErrorHandlerService
  ) { }

  /** List pages backing the correction screens, e.g. payrollRevertAdjustmentList. */
  getListPageData(payload: ListGridRequestPayload): Observable<any> {
    return this.post(ENDPOINTS.PAYROLL_CORRECTION.LIST_PAGE_DATA, payload);
  }

  /** Reprocesses denied / good-to-pay lines that have not been sent to NAV. */
  reprocess(programCode: string, dataType: CorrectionDataType, notes: string, dataIds: number[]): Observable<any> {
    return this.post(ENDPOINTS.PAYROLL_CORRECTION.REPROCESS, { programCode, dataType, notes, dataIds }, true);
  }

  /** Reverts adjusted (AD) / voided (VD) lines back to their pre-adjustment state. */
  revertAdjustment(programCode: string, dataType: CorrectionDataType, notes: string, dataIds: number[]): Observable<any> {
    return this.post(ENDPOINTS.PAYROLL_CORRECTION.REVERT_ADJUSTMENT, { programCode, dataType, notes, dataIds }, true);
  }

  /**
   * Voids a cheque that was voided and unapplied in NAV: all its timesheet and invoice lines are
   * marked reversed and their authorization is given back.
   */
  chequeVoid(programCode: string, chequeNumber: string, providerId: number, reasonCode: string): Observable<any> {
    return this.post(
      ENDPOINTS.PAYROLL_CORRECTION.CHEQUE_VOID,
      { programCode, chequeNumber, providerId, actionLkp: 'PV', reasonCode },
      true
    );
  }

  /** Timesheet and invoice lines paid by a cheque. */
  getChequeLines(programCode: string, chequeNumber: string, providerId: number): Observable<any> {
    const query = new URLSearchParams({ programCode, chequeNumber, providerId: String(providerId) });
    return this.httpClient
      .get(`${ENDPOINTS.PAYROLL_CORRECTION.CHEQUE_LINES}?${query}`)
      .pipe(
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  /** Resets lines already sent to NAV (the NAV data is deleted) so they can be paid or denied again. */
  resetNav(programCode: string, dataType: CorrectionDataType, notes: string, dataIds: number[]): Observable<any> {
    return this.post(ENDPOINTS.PAYROLL_CORRECTION.RESET_NAV, { programCode, dataType, mode: 'RESET', notes, dataIds }, true);
  }

  private post(url: string, payload: object, isWriteRequest?: boolean): Observable<any> {
    return this.httpClient
      .post(url, payload, isWriteRequest)
      .pipe(
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }
}
